import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import CallEndIcon from "@mui/icons-material/CallEnd";
import SmsIcon from "@mui/icons-material/Sms";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import LanguageIcon from "@mui/icons-material/Language";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import HttpService from "../../services/HttpService";
import UserService from "../../services/UserService";

async function fetchConversation(conversationId) {
  const res = await HttpService.getAxiosClient().get(
    window.API_URL + `/chat/support/conversations/all`
  );
  return (res.data || []).find((c) => c.id === conversationId) || null;
}

async function fetchHistory(conversationId) {
  const res = await HttpService.getAxiosClient().get(
    window.API_URL + `/chat/support/${conversationId}/history`
  );
  return res.data;
}

async function claimConversation(conversationId) {
  const res = await HttpService.getAxiosClient().post(
    window.API_URL + `/chat/support/${conversationId}/claim`
  );
  return res.data;
}

async function sendAgentMessage({ conversationId, content }) {
  const res = await HttpService.getAxiosClient().post(
    window.API_URL + `/chat/support/${conversationId}/message`,
    { content }
  );
  return res.data;
}

async function closeConversation(conversationId) {
  const res = await HttpService.getAxiosClient().post(
    window.API_URL + `/chat/support/${conversationId}/close`
  );
  return res.data;
}

function senderIcon(senderType) {
  if (senderType === "USER") return <PersonIcon fontSize="small" />;
  if (senderType === "AGENT") return <SupportAgentIcon fontSize="small" />;
  return <SmartToyIcon fontSize="small" />;
}

function senderColor(senderType) {
  if (senderType === "USER") return "#1976d2";
  if (senderType === "AGENT") return "#0d7f81";
  return "#757575";
}

function MessageBubble({ msg, userLabel, channel }) {
  const isRight = msg.senderType === "AGENT";
  const label =
    msg.senderType === "USER"
      ? userLabel || "User"
      : msg.senderType === "AGENT"
        ? "Agent"
        : "Assistant";

  // Prefer per-message originChannel stored in metadata; fall back to conversation channel.
  let originChannel = channel;
  if (msg.metadata) {
    try {
      const meta = JSON.parse(msg.metadata);
      if (meta.originChannel) originChannel = meta.originChannel;
    } catch (_) {}
  }
  const showSms = originChannel === "SMS";
  const showInApp = originChannel === "IN_APP" || originChannel === "APP";
  const showWeb = originChannel === "WEB";
  const showIvr = originChannel === "IVR";
  return (
    <Box
      display="flex"
      flexDirection={isRight ? "row-reverse" : "row"}
      alignItems="flex-end"
      gap={1}
      mb={1.5}
    >
      <Avatar
        sx={{ width: 28, height: 28, bgcolor: senderColor(msg.senderType) }}
      >
        {senderIcon(msg.senderType)}
      </Avatar>
      <Box
        sx={{
          maxWidth: "70%",
          bgcolor: isRight
            ? "#0d7f81"
            : msg.senderType === "USER"
              ? "#e3f2fd"
              : "#f0f0f0",
          color: isRight ? "white" : "text.primary",
          borderRadius: 2,
          px: 2,
          py: 1,
        }}
      >
        <Box display="flex" alignItems="center" gap={0.5} mb={0.3}>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            {label} ·{" "}
            {msg.timestamp ? new Date(msg.timestamp).toLocaleString() : ""}
          </Typography>
          {showSms && (
            <Chip
              icon={<SmsIcon sx={{ fontSize: "11px !important" }} />}
              label="SMS"
              size="small"
              sx={{
                height: 16,
                fontSize: 10,
                opacity: 0.75,
                "& .MuiChip-label": { px: 0.5 },
                "& .MuiChip-icon": { ml: 0.5 },
              }}
            />
          )}
          {showInApp && (
            <Chip
              icon={<PhoneIphoneIcon sx={{ fontSize: "11px !important" }} />}
              label="In-App"
              size="small"
              sx={{
                height: 16,
                fontSize: 10,
                opacity: 0.75,
                "& .MuiChip-label": { px: 0.5 },
                "& .MuiChip-icon": { ml: 0.5 },
              }}
            />
          )}
          {showWeb && (
            <Chip
              icon={<LanguageIcon sx={{ fontSize: "11px !important" }} />}
              label="Web"
              size="small"
              sx={{
                height: 16,
                fontSize: 10,
                opacity: 0.75,
                "& .MuiChip-label": { px: 0.5 },
                "& .MuiChip-icon": { ml: 0.5 },
              }}
            />
          )}
          {showIvr && (
            <Chip
              icon={<PhoneInTalkIcon sx={{ fontSize: "11px !important" }} />}
              label="IVR"
              size="small"
              sx={{
                height: 16,
                fontSize: 10,
                opacity: 0.75,
                "& .MuiChip-label": { px: 0.5 },
                "& .MuiChip-icon": { ml: 0.5 },
              }}
            />
          )}
        </Box>
        <Typography variant="body2">{msg.content}</Typography>
      </Box>
    </Box>
  );
}

export default function SupportChatConversation({
  conversationId: propConvId,
  onClose,
}) {
  const { conversationId: paramConvId } = useParams();
  const conversationId = propConvId || paramConvId;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [input, setInput] = useState("");
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [recordingBlobUrl, setRecordingBlobUrl] = useState(null);
  const bottomRef = useRef(null);

  const currentUser = UserService.getUsername?.() || "";

  const { data: conv, refetch: refetchConv } = useQuery(
    ["supportConv", conversationId],
    () => fetchConversation(conversationId),
    {
      refetchOnWindowFocus: false,
      refetchInterval: 10_000,
    }
  );

  const {
    data: history,
    isFetching: historyFetching,
    isError: historyError,
    refetch: refetchHistory,
  } = useQuery(
    ["supportHistory", conversationId],
    () => fetchHistory(conversationId),
    {
      refetchOnWindowFocus: false,
      refetchInterval: 10_000,
    }
  );

  // Scroll to bottom when messages load/update
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [history]);

  // Fetch recording as authenticated blob so the JWT is sent (plain <audio src>
  // doesn't include Authorization headers and would get a 401).
  // Fetch recording as authenticated blob so the JWT is sent (plain <audio src>
  // doesn't include Authorization headers and would get a 401).
  useEffect(() => {
    if (!conv?.hasRecording || !conversationId) return;
    let objectUrl;
    HttpService.getAxiosClient()
      .get(window.API_URL + `/chat/support/${conversationId}/recording`, {
        responseType: "blob",
      })
      .then((res) => {
        objectUrl = URL.createObjectURL(res.data);
        setRecordingBlobUrl(objectUrl);
      })
      .catch(() => setRecordingBlobUrl(null));
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [conv?.hasRecording, conversationId]);

  const claimMutation = useMutation(() => claimConversation(conversationId), {
    onSuccess: () => {
      queryClient.invalidateQueries(["supportConv", conversationId]);
      queryClient.invalidateQueries(["supportConversations"]);
    },
  });

  const sendMutation = useMutation(
    ({ content }) => sendAgentMessage({ conversationId, content }),
    {
      onSuccess: () => {
        setInput("");
        refetchHistory();
      },
    }
  );

  const closeMutation = useMutation(() => closeConversation(conversationId), {
    onSuccess: () => {
      setCloseDialogOpen(false);
      queryClient.invalidateQueries(["supportConv", conversationId]);
      queryClient.invalidateQueries(["supportConversations"]);
      queryClient.invalidateQueries(["supportAllConversations"]);
      refetchConv();
    },
  });

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    sendMutation.mutate({ content: trimmed });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isAssignedToMe = conv?.assignedAgentId === currentUser;
  const isClosed = conv?.status === "CLOSED";

  return (
    <Box
      p={3}
      display="flex"
      flexDirection="column"
      height={onClose ? "100%" : "calc(100vh - 140px)"}
      boxSizing="border-box"
    >
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <IconButton
          size="small"
          aria-label={onClose ? "Close conversation" : "Back to support chat"}
          onClick={() => (onClose ? onClose() : navigate("/supportChat"))}
        >
          {onClose ? <CloseIcon /> : <ArrowBackIcon />}
        </IconButton>
        <SupportAgentIcon sx={{ color: "#0d7f81" }} />
        <Typography variant="h6" fontWeight="bold" flex={1}>
          Conversation
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontFamily: "monospace", color: "text.secondary", mr: 1 }}
        >
          {conversationId}
        </Typography>
        {conv && (
          <Chip
            label={
              conv.status === "BOT"
                ? "Assistant"
                : conv.status === "HUMAN"
                  ? "Agent"
                  : conv.status === "ESCALATED_CALL"
                    ? "Escalated Call"
                    : conv.status
            }
            color={
              conv.status === "ESCALATED"
                ? "warning"
                : conv.status === "ESCALATED_CALL"
                  ? "error"
                  : conv.status === "HUMAN"
                    ? "success"
                    : "default"
            }
            size="small"
          />
        )}
        <Tooltip title="Refresh messages">
          <span>
            <IconButton
              size="small"
              onClick={() => {
                refetchHistory();
                refetchConv();
              }}
              disabled={historyFetching}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {!isClosed && isAssignedToMe && (
          <Tooltip title="End conversation">
            <span>
              <IconButton
                size="small"
                onClick={() => setCloseDialogOpen(true)}
                sx={{ color: "error.main" }}
              >
                <CallEndIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      {/* Claim banner — hidden for IVR conversations since a phone call
           cannot be claimed or replied to by an agent via chat. */}
      {conv && !isAssignedToMe && !isClosed && conv.channel !== "IVR" && (
        <Alert
          severity="warning"
          sx={{ mb: 2, alignItems: "center" }}
          action={
            <Button
              variant="contained"
              size="small"
              onClick={() => claimMutation.mutate()}
              disabled={claimMutation.isLoading}
              sx={{
                bgcolor: "#0d7f81",
                "&:hover": { bgcolor: "#0a6466" },
                whiteSpace: "nowrap",
              }}
            >
              {claimMutation.isLoading ? (
                <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
              ) : null}
              {claimMutation.isLoading ? "Claiming…" : "Claim Conversation"}
            </Button>
          }
        >
          {conv.assignedAgentId
            ? `Assigned to ${conv.assignedAgentId}. Claim to take over.`
            : 'This conversation is unassigned. Click "Claim Conversation" to reply.'}
        </Alert>
      )}

      {historyError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load message history.
        </Alert>
      )}

      {/* Message list */}
      <Paper
        elevation={1}
        sx={{ flex: 1, overflowY: "auto", p: 2, mb: 2, bgcolor: "#fafafa" }}
      >
        {historyFetching && !history ? (
          <Box display="flex" justifyContent="center" pt={4}>
            <CircularProgress size={24} />
          </Box>
        ) : history && history.length > 0 ? (
          <>
            {history.map((msg) => (
              <MessageBubble
                key={msg.id}
                msg={msg}
                channel={conv?.channel}
                userLabel={
                  conv
                    ? [conv.customerFirstName, conv.customerLastName]
                        .filter(Boolean)
                        .join(" ") ||
                      conv.customerEmail ||
                      conv.guestRef ||
                      conv.ownerId ||
                      "User"
                    : "User"
                }
              />
            ))}
            {conv?.hasRecording && recordingBlobUrl && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 2,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "#f0f4f8",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <HeadphonesIcon
                  fontSize="small"
                  sx={{ color: "error.main", flexShrink: 0 }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", flexShrink: 0 }}
                >
                  Call recording
                </Typography>
                <audio
                  controls
                  src={recordingBlobUrl}
                  style={{ flex: 1, minWidth: 0, height: 32 }}
                />
              </Box>
            )}
            {conv?.callTranscript && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "#f0f4f8",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", display: "block", mb: 0.5 }}
                >
                  Call transcript
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {conv.callTranscript}
                </Typography>
              </Box>
            )}
            <div ref={bottomRef} />
          </>
        ) : (
          <Typography color="text.secondary" align="center" pt={4}>
            No messages yet.
          </Typography>
        )}
      </Paper>

      <Divider sx={{ mb: 2 }} />

      {/* Reply box */}
      <Box display="flex" gap={1} alignItems="flex-end">
        <TextField
          multiline
          maxRows={4}
          fullWidth
          size="small"
          placeholder={
            isClosed
              ? "Conversation is closed"
              : isAssignedToMe
                ? "Type a reply…"
                : "Claim the conversation to reply"
          }
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={!isAssignedToMe || isClosed || sendMutation.isLoading}
        />
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={
            !isAssignedToMe ||
            isClosed ||
            !input.trim() ||
            sendMutation.isLoading
          }
          sx={{
            bgcolor: "#0d7f81",
            "&:hover": { bgcolor: "#0a6466" },
            minWidth: 48,
            height: 40,
          }}
        >
          {sendMutation.isLoading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <SendIcon fontSize="small" />
          )}
        </Button>
      </Box>

      {/* End Chat confirmation dialog */}
      <Dialog
        open={closeDialogOpen}
        onClose={() => setCloseDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>End conversation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will close the conversation for the customer. They will no
            longer be able to send messages. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCloseDialogOpen(false)}
            disabled={closeMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => closeMutation.mutate()}
            disabled={closeMutation.isLoading}
          >
            {closeMutation.isLoading ? (
              <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
            ) : null}
            {closeMutation.isLoading ? "Closing…" : "End Chat"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
